import { createContext, useContext, useEffect, useState } from 'react';

const THEME_PRESETS = {
  indigo: {
    label: 'Indigo',
    accent: '#5557E8',
    accent2: '#4547CF',
    accentHot: '#4849CF',
    accentDark: '#7C7EF5',
    accentHotDark: '#A5A7FF',
    accentSoftLight: '#EEF0FF',
    accentSoftDark: '#1F2350',
  },
  blue: {
    label: 'Blue',
    accent: '#2563EB',
    accent2: '#1D4ED8',
    accentHot: '#1D4ED8',
    accentDark: '#60A5FA',
    accentHotDark: '#93C5FD',
    accentSoftLight: '#EAF1FF',
    accentSoftDark: '#16254A',
  },
  violet: {
    label: 'Violet',
    accent: '#7C3AED',
    accent2: '#6D28D9',
    accentHot: '#6D28D9',
    accentDark: '#A78BFA',
    accentHotDark: '#C4B5FD',
    accentSoftLight: '#F3EDFF',
    accentSoftDark: '#2A1D4D',
  },
  emerald: {
    label: 'Emerald',
    accent: '#0F9F6E',
    accent2: '#0B7F58',
    accentHot: '#0B7F58',
    accentDark: '#34D399',
    accentHotDark: '#6EE7B7',
    accentSoftLight: '#E6F7F0',
    accentSoftDark: '#0F3328',
  },
};

const DEFAULT_CONFIG = {
  version: 4,
  mode: 'light',
  preset: 'indigo',
  density: 'comfortable',
  radius: 'modern',
  customAccent: '',
};

const ThemeContext = createContext({
  theme: 'light',
  config: DEFAULT_CONFIG,
  presets: THEME_PRESETS,
  toggle: () => {},
  setMode: () => {},
  updateTheme: () => {},
  resetTheme: () => {},
});

function readConfig() {
  try {
    const saved = JSON.parse(localStorage.getItem('crm-theme-config') || '{}');
    const legacyMode = localStorage.getItem('crm-theme');
    if (saved.version !== DEFAULT_CONFIG.version) {
      return DEFAULT_CONFIG;
    }
    return { ...DEFAULT_CONFIG, ...(legacyMode && { mode: legacyMode }), ...saved };
  } catch {
    return DEFAULT_CONFIG;
  }
}

function contrastText(hex = '') {
  const clean = String(hex).replace('#', '');
  if (clean.length !== 6) return '#FFFFFF';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160 ? '#12182B' : '#FFFFFF';
}

function applyTheme(config) {
  const root = document.documentElement;
  const mode = config.mode === 'dark' ? 'dark' : 'light';
  const dark = mode === 'dark';
  const preset = THEME_PRESETS[config.preset] || THEME_PRESETS.indigo;
  const uiAccent = dark ? preset.accentDark : preset.accent;
  // Tenant brand colour tints the logo mark; the UI accent stays on the preset for contrast.
  const brandAccent = config.customAccent || preset.accent;

  root.setAttribute('data-theme', mode);
  root.setAttribute('data-density', config.density || 'comfortable');
  root.setAttribute('data-radius', config.radius || 'modern');
  root.style.setProperty('--accent', uiAccent);
  root.style.setProperty('--accent2', dark ? preset.accent : preset.accent2);
  root.style.setProperty('--accentHot', dark ? preset.accentHotDark : preset.accentHot);
  root.style.setProperty('--accentText', contrastText(uiAccent));
  root.style.setProperty('--brandAccent', brandAccent);
  root.style.setProperty('--brandAccentText', contrastText(brandAccent));
  root.style.setProperty('--accentSoft', dark ? preset.accentSoftDark : preset.accentSoftLight);
  root.style.setProperty('--accentMist', `${uiAccent}2E`);
  root.style.setProperty('--glow', `0 0 0 3px ${uiAccent}2E`);
}

export function ThemeProvider({ children }) {
  const [config, setConfig] = useState(readConfig);
  const theme = config.mode;

  useEffect(() => {
    applyTheme(config);
    localStorage.setItem('crm-theme', config.mode);
    localStorage.setItem('crm-theme-config', JSON.stringify(config));
  }, [config]);

  const updateTheme = (patch) => setConfig((current) => ({ ...current, ...patch }));
  const setMode = (mode) => updateTheme({ mode });
  const toggle = () => setConfig(t => ({ ...t, mode: t.mode === 'dark' ? 'light' : 'dark' }));
  const resetTheme = () => setConfig(DEFAULT_CONFIG);

  return (
    <ThemeContext.Provider value={{ theme, config, presets: THEME_PRESETS, toggle, setMode, updateTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
